import React, { useState, useEffect } from "react";
import apiService from "../../services/apiService";


interface Archive {
  key: string;
  size: number;
  lastModified: string;
  signedUrl: string;
}

const ArchiveDocuments: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [archives, setArchives] = useState<Archive[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchArchives();
  }, [selectedYear]);

  const fetchArchives = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/operators/archive-documents/${selectedYear}`);
      setArchives(response.data.archives || []);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setArchives([]);
      } else {
        console.error("Error fetching archives:", error);
        alert("Failed to fetch archives");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm(`Are you sure you want to archive all documents for ${selectedYear}? This will create a zip file in the archive folder.`)) {
      return;
    }

    setArchiving(true);
    setProgress(0);
    setProgressMessage('Starting...');
    
    try {
      const response = await apiService.post(`/operators/archive-documents/${selectedYear}`);
      const data: any = response.data;
      const jobId = data.jobId;
      setCurrentJobId(jobId);
      
      // Poll for progress
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await apiService.get(`/operators/archive-documents/status/${jobId}`);
          const status: any = statusResponse.data;
          
          setProgress(status.progress || 0);
          setProgressMessage(status.message || '');
          
          if (status.status === 'completed') {
            clearInterval(pollInterval);
            setArchiving(false);
            setProgress(100);
            alert(`Successfully archived ${status.fileCount} documents`);
            
            // Auto-download
            if (status.signedUrl) {
              window.open(status.signedUrl, "_blank");
            }
            
            // Refresh archives list
            await fetchArchives();
            setCurrentJobId(null);
          } else if (status.status === 'error') {
            clearInterval(pollInterval);
            setArchiving(false);
            alert(`Error: ${status.message}`);
            setCurrentJobId(null);
          }
        } catch (err) {
          console.error('Error polling status:', err);
        }
      }, 2000); // Poll every 2 seconds
      
    } catch (error: any) {
      console.error("Error starting archive:", error);
      alert(error.response?.data?.message || "Failed to start archive");
      setArchiving(false);
    }
  };

  const handleDownload = (signedUrl: string, key: string) => {
    const filename = key.split('/').pop() || 'archive.zip';
    const link = document.createElement('a');
    link.href = signedUrl;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handleDeleteDocuments = async () => {
    setShowDeleteConfirm(false);
    setDeleting(true);
    
    try {
      // First delete from S3
      const s3Response = await apiService.delete(`/operators/archive-documents/cleanup/${selectedYear}`);
      const s3Data: any = s3Response.data;
      
      // Then delete from database
      const dbResponse = await apiService.delete(`/operators/archive-documents/cleanup-db/${selectedYear}`);
      const dbData: any = dbResponse.data;
      
      alert(`Successfully deleted:\n- ${s3Data.deletedCount} file(s) from S3 (documents/${selectedYear}/)\n- ${dbData.deletedCount} record(s) from database`);
    } catch (error: any) {
      console.error("Error deleting documents:", error);
      alert(error.response?.data?.message || "Failed to delete documents");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteArchive = async (key: string) => {
    const filename = key.split('/').pop();
    const year = key.split('/')[1];
    
    if (!window.confirm(`Are you sure you want to delete ${filename}? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiService.delete(`/operators/archive-documents/${year}/${filename}`);
      alert(`Successfully deleted ${filename}`);
      await fetchArchives();
    } catch (error: any) {
      console.error("Error deleting archive:", error);
      alert(error.response?.data?.message || "Failed to delete archive");
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2 mt-8">Archive Documents</h1>
        <p className="text-gray-600">
          Archive all client documents by year into a single zip file for easy download and storage.
        </p>
      </div>

      <div className="bg-white shadow rounded-lg mb-6">
        <div className="p-6">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">Select Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <button
                onClick={handleArchive}
                disabled={archiving || deleting}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed mr-3"
              >
                {archiving ? (
                  <>
                    <svg className="inline animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Archiving... {progress}%
                  </>
                ) : (
                  <>
                    <i className="fas fa-archive mr-2"></i>
                    Create Archive
                  </>
                )}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={archiving || deleting}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <>
                    <svg className="inline animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash mr-2"></i>
                    Delete All Documents
                  </>
                )}
              </button>
            </div>
          </div>
          
          {archiving && (
            <div className="mt-4">
              <div className="mb-2 flex justify-between items-center">
                <span className="text-sm font-medium text-blue-700">{progressMessage}</span>
                <span className="text-sm font-medium text-blue-700">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}
          
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <i className="fas fa-info-circle mr-2"></i>
              This will create a zip file containing all documents from documents/{selectedYear}/ and store it in archive/{selectedYear}/. 
              The archive will be automatically downloaded after creation.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Existing Archives</h2>
          
          {loading ? (
            <div className="text-center py-8">
              <svg className="inline animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : archives.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <i className="fas fa-folder-open text-4xl mb-2"></i>
              <p>No archives found for {selectedYear}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Filename
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {archives.map((archive, index) => {
                    const filename = archive.key.split('/').pop() || 'archive.zip';
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <i className="fas fa-file-archive text-gray-400 mr-2"></i>
                            <span className="text-sm font-medium text-gray-900">
                              {filename}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatFileSize(archive.size)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(archive.lastModified)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleDownload(archive.signedUrl, archive.key)}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded mr-2"
                          >
                            <i className="fas fa-download mr-2"></i>
                            Download
                          </button>
                          <button
                            onClick={() => handleDeleteArchive(archive.key)}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
                          >
                            <i className="fas fa-trash mr-2"></i>
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <i className="fas fa-exclamation-triangle text-red-600 text-xl"></i>
                </div>
                <h3 className="ml-4 text-lg font-semibold text-gray-900">Confirm Delete</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete ALL documents from <strong>documents/{selectedYear}/</strong>?
                <br /><br />
                This will delete:
                <br />
                • All files from S3 (documents/{selectedYear}/)
                <br />
                • All database records from htax_tax_documents table for {selectedYear}
                <br /><br />
                ⚠️ <strong>Warning:</strong> This action cannot be undone. Make sure you have created an archive before deleting.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteDocuments}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchiveDocuments;
